import { DxcSceneName } from '../dxc.types';

/**
 * Traducción de los códigos del sistema al idioma del usuario.
 *
 * Existe porque el mensaje que devuelve el backend **no es un mensaje para el
 * usuario**, y enseñárselo tal cual es lo que hace que un error no se pueda
 * accionar. Tres ejemplos reales del repositorio, copiados literalmente:
 *
 *   · «El valor del campo SubTotal debe ser cero '0'.»
 *   · «El campo TipoCambio no se debe registrar en el CFDI.»
 *   · «El valor del campo debe Tipo de Comprobante ser "P" (Pago)»
 *
 * Los tres describen el **esquema del SAT**, no lo que le pasa a quien está
 * mirando la pantalla. Y los tres tienen algo peor en común: **el usuario no
 * puede arreglar ninguno** — esos campos los arma dexCarga, no él. Un error que
 * le pide corregir algo que no controla no es solo confuso, es una trampa.
 *
 * De ahí que el eje de esta tabla no sea «qué falló» sino **quién puede
 * arreglarlo**:
 *
 *   · `fix: 'usuario'` → hay una acción concreta, y el botón lleva a ella.
 *   · `fix: 'soporte'` → no la hay. El diálogo lo dice sin rodeos y ofrece
 *     copiar el detalle, en vez de mandarlo a buscar un campo inexistente.
 *
 * El volcado del backend no se tira: baja al `detail` plegado, que es donde
 * sirve — para pegarlo en un ticket.
 */
export type DxcErrorHint = {
    /** Qué pasó, en la lengua del usuario. Nunca el nombre de un campo del XML. */
    message: string;
    /** Qué hacer ahora. Es el rótulo del botón, así que empieza por un verbo. */
    action?: string;
    /** Quién puede arreglarlo. Decide el tono del diálogo y qué botón sale. */
    fix: 'usuario' | 'soporte';
    /** Solo cuando el dominio tiene una escena más precisa que la genérica. */
    scene?: DxcSceneName;
};

export const DXC_ERROR_HINTS: Record<string, DxcErrorHint> = {

    // ── Sesión y permisos ──────────────────────────────────────────────────
    Unauthorized:             { fix: 'usuario', message: 'Tu cuenta no tiene permiso para esta acción. Pídeselo a quien administra la empresa en dexCarga.', action: 'Entendido' },
    MissingToken:             { fix: 'usuario', message: 'Tu sesión terminó. Vuelve a entrar y repite la operación; no se perdió nada de lo que ya guardaste.', action: 'Volver a entrar' },
    InvalidToken:             { fix: 'usuario', message: 'Tu sesión terminó. Vuelve a entrar y repite la operación; no se perdió nada de lo que ya guardaste.', action: 'Volver a entrar' },
    InvalidUsernameOrPassword:{ fix: 'usuario', message: 'El correo o la contraseña no coinciden. Revisa los dos y vuelve a intentarlo.', action: 'Volver a intentar' },
    '401':                    { fix: 'usuario', message: 'Tu sesión terminó. Vuelve a entrar y repite la operación.', action: 'Volver a entrar' },
    '403':                    { fix: 'usuario', message: 'Tu cuenta no tiene permiso para esta acción. Pídeselo a quien administra la empresa en dexCarga.', action: 'Entendido' },

    // ── El registro ya no está, o ya está ──────────────────────────────────
    DrayageNotFound:          { fix: 'usuario', message: 'Este flete ya no existe. Puede que alguien de tu empresa lo haya cancelado mientras lo tenías abierto.', action: 'Ver mis fletes' },
    CompanyNotFound:          { fix: 'usuario', message: 'La empresa ya no está disponible. Actualiza la página para ver los datos al día.', action: 'Actualizar' },
    VehicleNotFound:          { fix: 'usuario', message: 'La unidad ya no está en tu flota. Puede que alguien la haya dado de baja.', action: 'Ver la flota' },
    ContainerNotFound:        { fix: 'usuario', message: 'El contenedor ya no está en este flete.', action: 'Entendido' },
    PhoneAlreadyExists:       { fix: 'usuario', message: 'Ese teléfono ya está registrado en otra cuenta. Usa otro número o recupera la cuenta que lo tiene.', action: 'Corregir el teléfono' },
    CCPAlreadyCancelled:      { fix: 'usuario', message: 'Esta carta porte ya estaba cancelada. No hace falta cancelarla otra vez.', action: 'Entendido', scene: 'sello-rechazado' },
    CPagoAlreadyCancelled:    { fix: 'usuario', message: 'Este complemento de pago ya estaba cancelado.', action: 'Entendido' },
    '404':                    { fix: 'usuario', message: 'El registro ya no existe. Actualiza la página para ver los datos al día.', action: 'Actualizar' },
    '409':                    { fix: 'usuario', message: 'Alguien más cambió este registro mientras lo tenías abierto. Actualiza y vuelve a intentarlo para no pisar su cambio.', action: 'Actualizar' },

    // ── Sello digital de la empresa ────────────────────────────────────────
    EntityCSDNotFound:        { fix: 'usuario', message: 'Tu empresa todavía no tiene cargado el sello digital (CSD). Sin él el SAT no puede timbrar.', action: 'Cargar el sello', scene: 'sello-rechazado' },
    CertNotFound:             { fix: 'usuario', message: 'Falta el archivo .cer del sello digital.', action: 'Cargar el certificado', scene: 'sello-rechazado' },
    KeyNotFound:              { fix: 'usuario', message: 'Falta el archivo .key del sello digital.', action: 'Cargar la llave', scene: 'sello-rechazado' },
    InvalidCertFile:          { fix: 'usuario', message: 'El archivo .cer no es válido o no corresponde al RFC de tu empresa. Descárgalo otra vez del portal del SAT.', action: 'Volver a cargarlo', scene: 'sello-rechazado' },
    InvalidKeyFile:           { fix: 'usuario', message: 'La llave .key no abre con esa contraseña, o no es la pareja del certificado que cargaste.', action: 'Volver a cargarla', scene: 'sello-rechazado' },
    CompanyNotRegistered:     { fix: 'usuario', message: 'Tu empresa aún no está dada de alta ante el PAC. Completa los datos fiscales para poder timbrar.', action: 'Completar los datos' },

    // ── Timbrado ───────────────────────────────────────────────────────────
    CFDI40102:                { fix: 'usuario', message: 'El régimen fiscal del receptor no corresponde con su RFC. Corrígelo en los datos del cliente y vuelve a timbrar.', action: 'Corregir el cliente', scene: 'sello-rechazado' },
    NoInvoicesProvided:       { fix: 'usuario', message: 'No elegiste ninguna factura. Marca al menos una para generar el complemento.', action: 'Elegir facturas' },

    // ── Archivos ───────────────────────────────────────────────────────────
    '413':                    { fix: 'usuario', message: 'El archivo pesa demasiado. Comprímelo o sube uno de menos de 10 MB.', action: 'Elegir otro archivo' },

    // ── Red ────────────────────────────────────────────────────────────────
    '0':                      { fix: 'usuario', message: 'No hay conexión con dexCarga. Revisa tu internet y vuelve a intentarlo; nada de lo que hiciste se perdió.', action: 'Volver a intentar' },
    '504':                    { fix: 'usuario', message: 'La operación tardó más de lo normal y se cortó. Vuelve a intentarlo en un momento.', action: 'Volver a intentar' },

    // ── Lo que el usuario NO puede arreglar ────────────────────────────────
    // Estas son las que justifican la tabla entera. `CRP201xx` y `CRP202xx`
    // validan el XML del complemento de pago contra el esquema del SAT, y ese
    // XML lo arma dexCarga. Pedirle al usuario que «registre el campo
    // TipoCambio» es pedirle que arregle algo que no toca.
    CRP201:                   { fix: 'soporte', message: 'El complemento de pago que preparamos no pasó una validación del SAT. Esto lo generamos nosotros, así que no es algo que puedas corregir desde aquí.' },
    CRP202:                   { fix: 'soporte', message: 'El complemento de pago que preparamos no pasó una validación del SAT. Esto lo generamos nosotros, así que no es algo que puedas corregir desde aquí.' },
    '500':                    { fix: 'soporte', message: 'Algo falló de nuestro lado. No es nada que hayas hecho mal y no se perdió lo que ya estaba guardado.' },
    '502':                    { fix: 'soporte', message: 'Algo falló de nuestro lado. No es nada que hayas hecho mal y no se perdió lo que ya estaba guardado.' },
    '503':                    { fix: 'soporte', message: 'dexCarga está en mantenimiento. Vuelve a intentarlo en unos minutos.' },
};

/**
 * Busca la traducción de un código: primero exacta, después por el prefijo más
 * largo que case.
 *
 * El prefijo no es comodidad: las familias `CRP201xx` y `CRP202xx` son más de
 * cuarenta códigos que dicen exactamente lo mismo al usuario —«el XML que
 * armamos no pasó una validación»— y difieren solo en qué campo del esquema
 * falló, que es justo el dato que NO va en la pantalla. Escribir cuarenta filas
 * idénticas garantiza que alguna se quede sin actualizar el día que cambie el
 * texto.
 */
export function resolveDxcErrorHint(code: string | undefined): DxcErrorHint | undefined {
    if (!code) return undefined;
    const key = code.trim();
    if (DXC_ERROR_HINTS[key]) return DXC_ERROR_HINTS[key];

    let best: DxcErrorHint | undefined;
    let bestLength = 0;
    for (const [prefix, hint] of Object.entries(DXC_ERROR_HINTS)) {
        if (prefix.length > bestLength && prefix.length < key.length && key.startsWith(prefix)) {
            best = hint;
            bestLength = prefix.length;
        }
    }
    return best;
}
